import { ChangeEvent, useContext, useEffect, useRef } from 'react';
import clsx from 'clsx';
import useOnclickOutside from 'react-cool-onclickoutside';

import { InputContext } from 'src/contexts/input.context';
import { mockFn } from 'src/tools/fn.tools';
import { UseIcon } from 'src/components/UseIcon';
import { CloseButton } from 'src/components/Button';
import { ifTrue } from 'src/tools/logic.tools';

import classes from './InlineTextarea.module.scss';
import * as React from 'react';

export function InlineTextarea() {
  const {
    classNames,
    value,
    onChange,
    onSubmit = mockFn,
    onDiscard = mockFn,
    placeholder,
    isDisabled,
    isReadonly,
    isAutoFocus,
    onClick,
  } = useContext(InputContext);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const ref = useOnclickOutside(onDiscard, {
    disabled: isReadonly,
    eventTypes: ['click'],
  });

  useEffect(() => {
    if (!isDisabled && !isReadonly && isAutoFocus) textareaRef.current?.focus();
  }, [isAutoFocus, isDisabled, isReadonly]);

  function handleOnClick(e: React.MouseEvent) {
    if (onClick) {
      e.stopPropagation();

      onClick();
    }
  }

  function handleSubmitOnClick(e: React.MouseEvent) {
    e.stopPropagation();

    onSubmit();
  }

  function handleDiscardOnClick(e: React.MouseEvent) {
    e.stopPropagation();

    onDiscard();
  }

  function handleOnChange(e: ChangeEvent<HTMLTextAreaElement>) {
    onChange(e.target.value);
  }

  function handleOnKeyUp(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Escape') onDiscard();
  }

  function renderEditIcon() {
    return <UseIcon className='default-icon' iconId='edit' />;
  }

  function renderButtons() {
    return (
      <>
        <UseIcon
          wrapElement='button'
          className={clsx(classes.submit, 'default-icon')}
          onClick={handleSubmitOnClick}
          iconId='check'
        />
        <CloseButton className={classes.close} onClick={handleDiscardOnClick} isSmall />
      </>
    );
  }

  return (
    <div
      ref={ref}
      data-testid='inline-textarea'
      className={clsx(classes.container, 'row a-center', classNames?.input, {
        disabled: isDisabled,
        'c-pointer': !isDisabled && isReadonly,
      })}
      onClick={handleOnClick}
    >
      <textarea
        ref={textareaRef}
        onChange={handleOnChange}
        readOnly={isReadonly}
        disabled={isDisabled}
        value={value}
        placeholder={placeholder}
        onKeyUp={handleOnKeyUp}
      />
      <div className={clsx(classes.buttons, 'row items-p8')}>{ifTrue(isReadonly, renderEditIcon, renderButtons)}</div>
    </div>
  );
}
